import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faClock,
  faCheck,
  faShip,
  faTruck,
  faPlane,
  faQuestion,
  faTimes,
  faDownload,
  faFileExcel,
  faClipboardList,
  faIdCard,
  faBuilding,
  faMapMarkerAlt,
  faFileInvoice,
  faExclamationTriangle,
  faInfoCircle,
  faChartLine,
  faRocket,
  faFlag,
  faHourglassHalf,
  faCheckCircle,
  faBell,
  faLightbulb,
  faArrowRight,
  faPlayCircle,
} from "@fortawesome/free-solid-svg-icons";

const ShipmentDetailModal = ({
  item,
  onClose,
  getTransportIcon,
  getEtaDate,
  getShipmentStatusText,
  formatDate,
  isShipmentDelayed,
  isShipmentCritical,
  isShipmentClosed,
}) => {
  const [showDelayDetails, setShowDelayDetails] = useState(false);
  const [showTimelineDetails, setShowTimelineDetails] = useState(false);

  if (!item) return null;

  // Format date for display
  const formatDetailDate = (date) => {
    if (!date) return "N/A";
    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return "N/A";

      const options = { year: "numeric", month: "short", day: "numeric" };
      return dateObj.toLocaleDateString(undefined, options);
    } catch (error) {
      console.error("Error formatting date:", error);
      return "N/A";
    }
  };

  // Gets formatted ETA date
  const etaDate = getEtaDate(item);
  const formattedEtaDate = etaDate ? formatDetailDate(etaDate) : "N/A";

  // Get status details
  const shipmentStatus = getShipmentStatusText(item);
  const statusClass = () => {
    if (shipmentStatus === "Critical Delay") return "bg-red-100 text-red-800";
    if (shipmentStatus === "Delayed") return "bg-amber-100 text-amber-800";
    if (shipmentStatus === "Upcoming") return "bg-blue-100 text-blue-800";
    return "bg-green-100 text-green-800";
  };

  // Calculate delay metrics
  const calculateDelayMetrics = () => {
    const etaDate = getEtaDate(item);
    if (!etaDate) return null;

    const today = new Date();
    const eta = new Date(etaDate);
    const diffTime = today - eta;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return {
      isDelayed: isShipmentDelayed && isShipmentDelayed(item),
      isCritical: isShipmentCritical && isShipmentCritical(item),
      isClosed: isShipmentClosed && isShipmentClosed(item),
      daysDelayed: diffDays > 0 ? diffDays : 0,
      etaDate: eta,
    };
  };

  const delayMetrics = calculateDelayMetrics();

  // Get delay reasons based on shipment data
  const getDelayReasons = () => {
    const reasons = [];

    // Check for documentation issues
    if (!item.header.detail?.traffic_data?.status || item.header.detail?.traffic_data?.status === "Pending") {
      reasons.push({
        category: "Documentation",
        reason: "Pending customs documentation",
        severity: "medium",
        icon: faFileInvoice
      });
    }

    // Check for payment issues
    if (!item.header.detail?.dates_data?.date_pedimento_paid && delayMetrics?.isDelayed) {
      reasons.push({
        category: "Payment",
        reason: "Awaiting pedimento payment",
        severity: "high",
        icon: faExclamationTriangle
      });
    }

    // Check for transport delays
    if (delayMetrics?.daysDelayed > 3) {
      reasons.push({
        category: "Transport",
        reason: "Significant transport delay detected",
        severity: "critical",
        icon: faHourglassHalf
      });
    }

    // Check for customs hold
    if (item.header.trafficStatus && item.header.trafficStatus.toLowerCase().includes('hold')) {
      reasons.push({
        category: "Customs",
        reason: "Shipment on customs hold",
        severity: "high",
        icon: faFlag
      });
    }

    return reasons;
  };

  // Get action recommendations
  const getActionRecommendations = (delayReasons) => {
    const actions = [];

    delayReasons.forEach(reason => {
      switch (reason.category) {
        case "Documentation":
          actions.push({
            priority: "High",
            action: "Review and complete missing customs documentation",
            responsible: "Customs Broker",
            icon: faFileInvoice
          });
          break;
        case "Payment":
          actions.push({
            priority: "Critical",
            action: "Process pedimento payment immediately",
            responsible: "Finance Team",
            icon: faRocket
          });
          break;
        case "Transport":
          actions.push({
            priority: "Medium",
            action: "Contact carrier for updated delivery schedule",
            responsible: "Logistics Coordinator",
            icon: faTruck
          });
          break;
        case "Customs":
          actions.push({
            priority: "High",
            action: "Follow up with customs broker on release status",
            responsible: "Customs Specialist",
            icon: faFlag
          });
          break;
      }
    });

    // Add general recommendations
    if (delayMetrics?.isCritical) {
      actions.push({
        priority: "Critical",
        action: "Escalate to operations manager",
        responsible: "Team Lead",
        icon: faBell
      });
    }

    return actions;
  };

  const delayReasons = getDelayReasons();
  const actionRecommendations = getActionRecommendations(delayReasons);

  // Get primary delay cause for streamlined display
  const getPrimaryDelayCause = () => {
    if (delayReasons.length === 0) return "Investigation required";
    const highPriorityReasons = delayReasons.filter(r => r.severity === "critical" || r.severity === "high");
    return highPriorityReasons.length > 0 ? highPriorityReasons[0].reason : delayReasons[0].reason;
  };

  // Get shipment stage for timeline
  const getShipmentStage = () => {
    const dates = item.header.detail?.dates_data || {};

    if (dates.date_pedimento_paid) return { stage: "Delivery", progress: 90 };
    if (dates.date_crossing) return { stage: "Payment", progress: 75 };
    if (dates.date_doda) return { stage: "Crossing", progress: 60 };
    if (dates.date_yard_outbound) return { stage: "DODA", progress: 45 };
    if (dates.date_init_loading) return { stage: "Loading", progress: 30 };
    if (dates.date_inbound) return { stage: "Documentation", progress: 15 };
    return { stage: "Receipt", progress: 5 };
  };

  const primaryCause = getPrimaryDelayCause();
  const currentStage = getShipmentStage();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-[#1A2654] to-blue-700 text-white px-6 py-4 rounded-t-lg flex justify-between items-center">
          <h2 className="text-xl font-bold flex items-center">
            <FontAwesomeIcon
              icon={
                getTransportIcon(item.header.pedimento?.pedimento_entry_point)
                  .icon
              }
              className="mr-2"
            />
            Shipment {item.header.reference}
          </h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} size="lg" />
          </button>
        </div>

        <div className="p-6">
          {/* Status Badge */}
          <div className="mb-6 flex justify-between items-center">
            <div
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusClass()}`}
            >
              <span>{shipmentStatus}</span>
            </div>
            <div className="text-sm text-gray-500">
              ETA: <span className="font-medium">{formattedEtaDate}</span>
            </div>
          </div>

          {/* Elegant Delay Analysis Section */}
          {delayMetrics && (delayMetrics.isDelayed || delayMetrics.isCritical) && (
            <div className="mb-6">
              {/* Streamlined Status Header */}
              <div className={`border-l-4 ${delayMetrics.isCritical ? 'border-red-500' : 'border-amber-500'} bg-gray-50 px-4 py-3 mb-4 rounded-r-md`}>
                <div className="flex items-center text-sm">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mr-3 ${
                    delayMetrics.isCritical ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'
                  }`}>
                    {delayMetrics.isCritical ? 'Critical' : 'Delayed'}
                  </span>
                  <span className="text-gray-600">•</span>
                  <span className="mx-2 font-medium text-gray-900">
                    {delayMetrics.daysDelayed} {delayMetrics.daysDelayed === 1 ? 'day' : 'days'} delayed
                  </span>
                  <span className="text-gray-600">•</span>
                  <span className="ml-2 text-gray-700">{primaryCause}</span>
                </div>
              </div>

              {/* Collapsible Timeline Progress */}
              <div className="mb-4">
                <button
                  onClick={() => setShowTimelineDetails(!showTimelineDetails)}
                  className="flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors mb-2"
                >
                  <FontAwesomeIcon
                    icon={showTimelineDetails ? faArrowRight : faArrowRight}
                    className={`mr-2 transition-transform ${showTimelineDetails ? 'rotate-90' : ''}`}
                  />
                  Progress Timeline - Currently at {currentStage.stage}
                </button>

                {/* Progress Bar */}
                <div className="flex items-center mb-2">
                  <div className="flex-1 bg-gray-200 rounded-full h-2 mr-3">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${
                        delayMetrics.isCritical ? 'bg-red-500' : 'bg-amber-500'
                      }`}
                      style={{ width: `${currentStage.progress}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-500">{currentStage.progress}%</span>
                </div>

                {/* Detailed Timeline (Collapsible) */}
                {showTimelineDetails && (
                  <div className="mt-3 pl-4 border-l border-gray-200 space-y-2">
                    {[
                      { name: "Receipt", key: "date_init" },
                      { name: "Documentation", key: "date_inbound" },
                      { name: "Loading", key: "date_init_loading" },
                      { name: "Yard Outbound", key: "date_yard_outbound" },
                      { name: "DODA", key: "date_doda" },
                      { name: "Crossing", key: "date_crossing" },
                      { name: "Payment", key: "date_pedimento_paid" }
                    ].map((stage, index) => {
                      const date = item.header.detail?.dates_data?.[stage.key];
                      return (
                        <div key={index} className="flex items-center text-xs">
                          <div className={`w-2 h-2 rounded-full mr-3 ${
                            date ? 'bg-green-500' : (currentStage.stage === stage.name ? 'bg-amber-500' : 'bg-gray-300')
                          }`} />
                          <span className={`mr-2 ${date ? 'text-gray-900' : 'text-gray-500'}`}>
                            {stage.name}
                          </span>
                          <span className="text-gray-500">
                            {date ? formatDetailDate(date) : 'Pending'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Unified Analysis Card */}
              <div className="bg-white border border-gray-200 rounded-md p-4 mb-4">
                <div className="mb-3">
                  <p className="text-sm text-gray-600 mb-1">Primary Cause</p>
                  <p className="font-medium text-gray-900">{primaryCause}</p>
                </div>

                <div className="flex items-center justify-between text-sm mb-3">
                  <span className="text-gray-600">
                    Impact: <span className={`font-medium ${delayMetrics.isCritical ? 'text-red-600' : 'text-amber-600'}`}>
                      {delayMetrics.isCritical ? 'High customer impact - escalation recommended' : 'Medium impact - monitor closely'}
                    </span>
                  </span>
                </div>

                <div className="text-sm text-gray-600 mb-3">
                  Resolution: <span className="font-medium text-gray-900">
                    {actionRecommendations.length > 0 ? '24-48 hours with action items below' : 'Investigation required'}
                  </span>
                </div>

                {/* Collapsible Details */}
                <button
                  onClick={() => setShowDelayDetails(!showDelayDetails)}
                  className="text-sm text-blue-600 hover:text-blue-800 transition-colors flex items-center"
                >
                  <FontAwesomeIcon
                    icon={showDelayDetails ? faArrowRight : faArrowRight}
                    className={`mr-1 transition-transform ${showDelayDetails ? 'rotate-90' : ''}`}
                  />
                  {showDelayDetails ? 'Hide details' : 'Show details'}
                </button>

                {showDelayDetails && (
                  <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
                    {/* All Delay Factors */}
                    {delayReasons.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">All Factors</p>
                        <div className="space-y-1">
                          {delayReasons.map((reason, index) => (
                            <div key={index} className="flex items-center text-sm">
                              <span className={`w-1.5 h-1.5 rounded-full mr-2 ${
                                reason.severity === 'critical' ? 'bg-red-500' :
                                reason.severity === 'high' ? 'bg-amber-500' : 'bg-yellow-500'
                              }`} />
                              <span className="text-gray-700">{reason.category}: {reason.reason}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Performance Metrics */}
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Performance</p>
                      <p className="text-sm text-gray-700">
                        Efficiency {delayMetrics.daysDelayed < 3 ? '85%' : delayMetrics.daysDelayed < 7 ? '65%' : '35%'} •
                        Customer Impact {delayMetrics.isCritical ? 'High' : 'Medium'} •
                        Resolution ETA {actionRecommendations.length > 0 ? '48h' : 'TBD'}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Clean Action Items */}
              {actionRecommendations.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Actions Required:</p>
                  <div className="space-y-1">
                    {actionRecommendations.map((action, index) => (
                      <div key={index} className="flex items-center text-sm">
                        <FontAwesomeIcon icon={faArrowRight} className="text-gray-400 mr-2 text-xs" />
                        <span className="text-gray-900">{action.action}</span>
                        <span className="text-gray-500 mx-2">({action.responsible})</span>
                        <span className={`px-1.5 py-0.5 rounded text-xs ${
                          action.priority === 'Critical' ? 'bg-red-100 text-red-700' :
                          action.priority === 'High' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {action.priority}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Single Action Button */}
              {delayMetrics.isCritical && (
                <div className="flex justify-end">
                  <button className="inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 transition-colors">
                    <FontAwesomeIcon icon={faRocket} className="mr-2" />
                    Escalate Issue
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Minimalistic Success Message */}
          {delayMetrics && !delayMetrics.isDelayed && !delayMetrics.isCritical && (
            <div className="mb-6 border-l-4 border-green-500 bg-green-50 px-4 py-3 rounded-r-md">
              <div className="flex items-center text-sm">
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mr-3" />
                <span className="font-medium text-green-800">
                  {delayMetrics.isClosed ? 'Shipment completed successfully' : 'On track - no delays detected'}
                </span>
              </div>
            </div>
          )}

          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 border-b pb-2 mb-3 flex items-center">
                <FontAwesomeIcon
                  icon={faClipboardList}
                  className="mr-2 text-blue-700"
                />
                Shipment Details
              </h3>

              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Reference:</span>
                  <span className="text-sm font-medium">
                    {item.header.reference}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Customer:</span>
                  <span className="text-sm font-medium">
                    {item.header.customer_name}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Customer ID:</span>
                  <span className="text-sm font-medium">
                    {item.header.customer_id}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">RFC:</span>
                  <span className="text-sm font-medium">
                    {item.header.customer_rfc}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Division:</span>
                  <span className="text-sm font-medium">
                    {item.header.customer_division || "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Operation Type:</span>
                  <span className="text-sm font-medium">
                    {item.header.operation_type === "1"
                      ? "Import"
                      : item.header.operation_type === "2"
                      ? "Export"
                      : item.header.operation_type || "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Weight:</span>
                  <span className="text-sm font-medium">
                    {item.header.weight ? `${item.header.weight} kg` : "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Packages:</span>
                  <span className="text-sm font-medium">
                    {item.header.total_packages || "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Traffic Status:</span>
                  <span className="text-sm font-medium">
                    {item.header.trafficStatus || "N/A"}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 border-b pb-2 mb-3 flex items-center">
                <FontAwesomeIcon
                  icon={faCalendarAlt}
                  className="mr-2 text-blue-700"
                />
                Dates
              </h3>

              <div className="space-y-3">
                {item.header.detail?.dates_data?.date_init && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Date Init:</span>
                    <span className="text-sm font-medium">
                      {formatDetailDate(
                        item.header.detail.dates_data.date_init
                      )}
                    </span>
                  </div>
                )}
                {item.header.detail?.dates_data?.date_inbound && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Date Inbound:</span>
                    <span className="text-sm font-medium">
                      {formatDetailDate(
                        item.header.detail.dates_data.date_inbound
                      )}
                    </span>
                  </div>
                )}
                {item.header.detail?.dates_data?.date_end && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Date End:</span>
                    <span className="text-sm font-medium">
                      {formatDetailDate(item.header.detail.dates_data.date_end)}
                    </span>
                  </div>
                )}
                {item.header.detail?.dates_data?.date_crossing && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">
                      Date Crossing:
                    </span>
                    <span className="text-sm font-medium">
                      {formatDetailDate(
                        item.header.detail.dates_data.date_crossing
                      )}
                    </span>
                  </div>
                )}
                {item.header.detail?.dates_data?.date_pedimento_paid && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">
                      Pedimento Paid:
                    </span>
                    <span className="text-sm font-medium">
                      {formatDetailDate(
                        item.header.detail.dates_data.date_pedimento_paid
                      )}
                    </span>
                  </div>
                )}
                {item.header.ebookingDates?.fechaETA && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">ETA Date:</span>
                    <span className="text-sm font-medium">
                      {formatDetailDate(item.header.ebookingDates.fechaETA)}
                    </span>
                  </div>
                )}
                {item.header.ebookingDates?.fechaPiso && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Fecha Piso:</span>
                    <span className="text-sm font-medium">
                      {formatDetailDate(item.header.ebookingDates.fechaPiso)}
                    </span>
                  </div>
                )}
              </div>

              <h3 className="text-lg font-medium text-gray-900 border-b pb-2 mb-3 mt-6 flex items-center">
                <FontAwesomeIcon
                  icon={faFileInvoice}
                  className="mr-2 text-blue-700"
                />
                Pedimento
              </h3>

              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Number:</span>
                  <span className="text-sm font-medium">
                    {item.header.pedimento?.pedimento_number || "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">License:</span>
                  <span className="text-sm font-medium">
                    {item.header.pedimento?.pedimento_license || "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Entry Point:</span>
                  <span className="text-sm font-medium">
                    {item.header.pedimento?.pedimento_entry_point || "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Transport Type:</span>
                  <span className="text-sm font-medium flex items-center">
                    <FontAwesomeIcon
                      icon={
                        getTransportIcon(
                          item.header.pedimento?.pedimento_entry_point
                        ).icon
                      }
                      className={`${
                        getTransportIcon(
                          item.header.pedimento?.pedimento_entry_point
                        ).color
                      } mr-1`}
                    />
                    {
                      getTransportIcon(
                        item.header.pedimento?.pedimento_entry_point
                      ).title
                    }
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Traffic Status Timeline */}
          {item.header.trafficStatusTimeline &&
            item.header.trafficStatusTimeline.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 border-b pb-2 mb-3 flex items-center">
                  <FontAwesomeIcon
                    icon={faClock}
                    className="mr-2 text-blue-700"
                  />
                  Status Timeline
                </h3>

                <div className="relative pl-8 space-y-6 before:absolute before:left-4 before:h-full before:border-l-2 before:border-gray-200">
                  {item.header.trafficStatusTimeline.map((status, index) => (
                    <div key={index} className="relative">
                      <div className="absolute left-[-2rem] top-0 flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 border-2 border-white">
                        <FontAwesomeIcon
                          icon={faCheck}
                          className="text-blue-600"
                        />
                      </div>
                      <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100">
                        <div className="font-medium">{status.status}</div>
                        <div className="text-sm text-gray-500">
                          {formatDetailDate(status.fechaSatus)}{" "}
                          {new Date(status.fechaSatus).toLocaleTimeString()}
                        </div>
                        {status.actualizadoPor && (
                          <div className="text-xs text-gray-400 mt-1">
                            Updated by: {status.actualizadoPor}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* Bill of Lading & Invoice Data */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Bill of Lading */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 border-b pb-2 mb-3 flex items-center">
                <FontAwesomeIcon
                  icon={faFileExcel}
                  className="mr-2 text-blue-700"
                />
                Bill of Lading
              </h3>

              {item.header.bill_of_lading_data &&
              item.header.bill_of_lading_data.length > 0 ? (
                <div className="space-y-3">
                  {item.header.bill_of_lading_data.map((bol, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex justify-between mb-2">
                        <span className="text-sm text-gray-500">
                          BOL Number:
                        </span>
                        <span className="text-sm font-medium">
                          {bol.bill_of_lading}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">BOL Type:</span>
                        <span className="text-sm font-medium">
                          {bol.bill_of_lading_type || "N/A"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-500 p-3 bg-gray-50 rounded-lg">
                  No bill of lading data available
                </div>
              )}
            </div>

            {/* Invoice Data */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 border-b pb-2 mb-3 flex items-center">
                <FontAwesomeIcon
                  icon={faFileInvoice}
                  className="mr-2 text-blue-700"
                />
                Invoice Data
              </h3>

              {item.header.detail?.invoice_data &&
              item.header.detail.invoice_data.length > 0 ? (
                <div className="space-y-3">
                  {item.header.detail.invoice_data.map((invoice, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex justify-between mb-2">
                        <span className="text-sm text-gray-500">
                          Invoice Number:
                        </span>
                        <span className="text-sm font-medium">
                          {invoice.invoice_number}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Supplier:</span>
                        <span className="text-sm font-medium">
                          {invoice.supplier || "N/A"}
                        </span>
                      </div>
                      {invoice.invoice_date && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Date:</span>
                          <span className="text-sm font-medium">
                            {formatDetailDate(invoice.invoice_date)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Value:</span>
                        <span className="text-sm font-medium">
                          {invoice.invoice_value} {invoice.currency}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Country:</span>
                        <span className="text-sm font-medium">
                          {invoice.invoice_country || "N/A"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-500 p-3 bg-gray-50 rounded-lg">
                  No invoice data available
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
            <button className="px-4 py-2 bg-blue-700 text-white rounded-md text-sm font-medium hover:bg-blue-800 transition-colors flex items-center space-x-2">
              <FontAwesomeIcon icon={faDownload} />
              <span>Export Details</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShipmentDetailModal;
